import * as Api from '@ViewModels';
import { getDisplayName } from '../../../../models/NameUtils';
import type { EventAutomationStartOutcome } from '../../../../queries/Surveys/useStartEventAutomationMutation';

export type CoverageState = 'retargeted' | 'neverMatches' | 'missed' | 'missedInactive' | 'missedPast';

export type CoverageTone = 'warning' | 'danger' | 'neutral';

export interface CoverageCopy {
	tone: CoverageTone;
	title: string;
	body: string;
	actionLabel?: string;
}

interface CoverageContext {
	isEventPast: boolean;
	/**
	 * The server evaluates the trigger flags against the published trigger (the draft only when never published), so
	 * while a draft changes the event or accepted responses they describe the old configuration, not what is on screen.
	 */
	isTriggerChangeUnpublished: boolean;
	/** A never-published automation also reports isAutomationLive false, which does not mean it was turned off. */
	hasPublishedVersion: boolean;
}

export function getCoverageState(
	coverage: Api.IEventAutomationCoverage,
	{ isEventPast, isTriggerChangeUnpublished, hasPublishedVersion }: CoverageContext
): CoverageState | null {
	if (!isTriggerChangeUnpublished) {
		if (coverage.triggerTargetsThisEvent === false) {
			return 'retargeted';
		}
		if (coverage.triggerCanNeverMatch === true) {
			return 'neverMatches';
		}
	}
	if (coverage.unenrolledCount <= 0) {
		return null;
	}
	if (isEventPast || coverage.isSurveyArchived) {
		return 'missedPast';
	}
	if (coverage.isAutomationLive === false && hasPublishedVersion) {
		return 'missedInactive';
	}
	return 'missed';
}

const joinName = (firstName?: string, lastName?: string) => [firstName, lastName].filter(Boolean).join(' ');

export function getRegistrantName(registrant: Api.IEventRegistrationSurveyResponse) {
	return (
		registrant.attendee?.name ||
		joinName(registrant.attendee?.firstName, registrant.attendee?.lastName) ||
		getDisplayName(registrant.contact as Api.IContact) ||
		registrant.attendee?.email ||
		'This registrant'
	);
}

export function getRegistrantEmail(registrant: Api.IEventRegistrationSurveyResponse) {
	return registrant.attendee?.email || registrant.contact?.primaryEmail?.value;
}

function describeDaysAway(daysUntilEvent?: number) {
	if (daysUntilEvent === undefined || daysUntilEvent < 0) {
		return '';
	}
	if (daysUntilEvent === 0) {
		return ' The event is today.';
	}
	if (daysUntilEvent === 1) {
		return ' The event is tomorrow.';
	}
	return ` The event is ${daysUntilEvent} days away.`;
}

function getShortfallCopy(
	coverage: Api.IEventAutomationCoverage,
	variant: 'banner' | 'inline',
	daysUntilEvent?: number
): CoverageCopy {
	const missed = coverage.unenrolledCount;
	const eligible = coverage.eligibleCount;

	if (variant === 'banner') {
		let title = `${missed} of the ${eligible} people attending this event are not enrolled`;
		if (missed === eligible) {
			title =
				missed === 1
					? 'The 1 person attending this event is not enrolled'
					: `None of the ${eligible} people attending this event are enrolled`;
		} else if (missed === 1) {
			title = `1 of the ${eligible} people attending this event is not enrolled`;
		}
		return {
			tone: 'warning',
			title,
			body: `They registered before this automation was set up, so they will not receive these emails.${describeDaysAway(daysUntilEvent)}`,
			actionLabel: missed === 1 ? 'Review the registrant' : 'Review registrants',
		};
	}

	if (missed === 1) {
		const name = coverage.registrants[0] ? getRegistrantName(coverage.registrants[0]) : 'This registrant';
		return {
			tone: 'warning',
			title: '1 person has already registered for this event',
			body: `This automation will only send to people who register from now on. ${name} will not be enrolled.`,
			actionLabel: 'Review the registrant',
		};
	}
	return {
		tone: 'warning',
		title: `${missed} people have already registered for this event`,
		body: `This automation will only send to people who register from now on. The ${missed} who already registered will not be enrolled.`,
		actionLabel: `Review the ${missed} registrants`,
	};
}

export function getCoverageCopy(
	state: CoverageState,
	coverage: Api.IEventAutomationCoverage,
	variant: 'banner' | 'inline',
	daysUntilEvent?: number
): CoverageCopy {
	const missed = coverage.unenrolledCount;

	switch (state) {
		case 'retargeted':
			return {
				tone: 'danger',
				title: 'This automation no longer runs for this event',
				body: 'Its trigger now points at a different event, so registrations here will not be enrolled, including new ones.',
				actionLabel: missed > 0 ? 'Review registrants' : undefined,
			};
		case 'neverMatches':
			return {
				tone: 'danger',
				title: 'This automation cannot enroll anyone',
				body: 'It is set to start only when someone is checked in, and checking in never starts an automation. Change the trigger so it starts when a contact responds with “I’m Attending”.',
			};
		case 'missedPast':
			return {
				tone: 'neutral',
				title:
					missed === 1
						? '1 registrant never received this automation'
						: `${missed} registrants never received this automation`,
				body: coverage.isSurveyArchived
					? 'They registered before it was set up. This event has been archived, so there is nothing left to send.'
					: 'They registered before it was set up. This event has passed, so there is nothing left to send.',
				actionLabel: 'See who was missed',
			};
		case 'missedInactive': {
			const copy = getShortfallCopy(coverage, variant);
			return { ...copy, body: `${copy.body} This automation is turned off, so it will not send to anyone.` };
		}
		default:
			return getShortfallCopy(coverage, variant, daysUntilEvent);
	}
}

export function getStartableContactIds(coverage: Api.IEventAutomationCoverage) {
	return [...new Set(coverage.registrants.map(registrant => registrant.contact?.id).filter(Boolean))];
}

const people = (count: number) => (count === 1 ? '1 person' : `${count} people`);

export function describeStartOutcome(outcome: EventAutomationStartOutcome): { success?: string; warning?: string } {
	const started = outcome.startedContactIds.length;
	const skipped = outcome.skippedContactIds.length;
	const failed = outcome.failedContactIds.length + outcome.notAttemptedContactIds.length;

	let success: string | undefined;
	if (started > 0) {
		success = `Started this automation for ${people(started)}.`;
	}
	if (skipped > 0) {
		const alreadyEnrolled = `${people(skipped)} ${skipped === 1 ? 'was' : 'were'} already enrolled.`;
		if (started > 0) {
			success = `${success} ${alreadyEnrolled}`;
		} else {
			success = failed > 0 ? alreadyEnrolled : 'Everyone selected was already enrolled.';
		}
	}

	let warning: string | undefined;
	if (failed > 0) {
		warning = `Couldn’t start it for ${people(failed)}. They’re still listed as not enrolled.`;
		if (outcome.stoppedEarlyError?.systemMessage) {
			warning += ` ${outcome.stoppedEarlyError.systemMessage}`;
		}
	}
	return { success, warning };
}
